import type {
  Control,
  FieldValues,
  SubmitHandler,
  UseFormReturn,
} from "react-hook-form";
import logoUrl from "@/shared/assets/laget-logo-orange-big.png";
import { Button } from "@/shared/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";

type Credentials = { username: string; password: string };

// Username + password form shared by login and join. Both pages' input is `Credentials`; only the
// transformed output differs, so only TOutput is generic.
export function CredentialForm<TOutput extends FieldValues>({
  form,
  onSubmit,
  heading,
  submitLabel,
  pending,
}: {
  form: UseFormReturn<Credentials, unknown, TOutput>;
  onSubmit: SubmitHandler<TOutput>;
  heading: string;
  submitLabel: string;
  pending: boolean;
}) {
  // shadcn's FormField assumes input and output types match; ours differ (a Zod transform), so cast
  // control to its input type. Both fields are plain strings pre- and post-transform, so this is sound.
  const control = form.control as unknown as Control<Credentials>;
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="surface-header flex w-full max-w-xl flex-col items-center gap-6 rounded-xl border border-border p-6 text-center shadow-xl md:flex-row md:gap-10 md:p-12 md:text-left">
        <img
          src={logoUrl}
          alt="LAGET"
          width={2138}
          height={1983}
          className="w-24 shrink-0 md:w-40"
        />
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full flex-1 space-y-3"
          >
            <h1 className="text-lg font-semibold">{heading}</h1>
            <FormField
              control={control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Användarnamn</FormLabel>
                  <FormControl>
                    <Input className="dark:bg-input" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lösenord</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      className="dark:bg-input"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" loading={pending}>
              {submitLabel}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
